/**
 * @file MessageModal.js
 * @module src/js/modals/MessageModal
 * @description
 * **Component to display toast messages in a modal overlay.**
 * - Renders toast messages at the top center of the viewport.
 * - Uses the `Toast` component for individual message display.
 * - Supports multiple messages stacking vertically.
 * - Also handles confirmation dialogs using the `ConfirmWindow` component.
 *
 * @version
 * <p>
 *  2025-12-02: First version with documentation. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 *  2025-12-02: Added confirmation dialog support. (Zong-Han Wu, zhwu@eda.ee.ntu.edu.tw)<br/>
 * </p>
 */
import { createPortal } from "react-dom";
import { useModal } from "../contexts/ModalProvider";
import Toast from "./Toast";
import ConfirmWindow from "./ConfirmWindow";

export default function MessageModal() {
    const { modalMessages, confirmDialog } = useModal();

    const messagePortalRoot = document.getElementById("message-portal-root");
    if (!messagePortalRoot) return null;
    
    const hasToasts = modalMessages && modalMessages.length > 0;
    const hasConfirm = !!confirmDialog;
    if (!hasToasts && !hasConfirm) return null;

    const toastStack = hasToasts ? (
        <div
            style={{
                position: "fixed",
                top: 0,
                left: 0,
                right: 0,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "8px",
                marginTop: "8px",
                pointerEvents: "none",
                zIndex: 10000,
            }}
        >
            {modalMessages.map((m) => (
                <Toast
                    key={m.id}
                    message={m}
                />
            ))}
        </div>
    ) : null;

    return createPortal(
        <>
            {toastStack}
            {hasConfirm && <ConfirmWindow />}
        </>,
        messagePortalRoot
    );
}
